import { useCallback, useEffect, useRef, useState } from 'react';

import useTranslations from '@/i18n/useTranslations';
import getCroppedImgData from '@/pages/artwork/artwork-header-mobile/options/download-template/functions/cropImage';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';

const MIN_SELECTION_SIZE = 24;
const DRAG_THRESHOLD = 3;

const HANDLE_DIRECTIONS = ['nw', 'ne', 'sw', 'se'];

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function resolveResizeSelection(startSelection, direction, deltaX, deltaY, bounds) {
  const right = startSelection.x + startSelection.width;
  const bottom = startSelection.y + startSelection.height;

  let nextX = startSelection.x;
  let nextY = startSelection.y;
  let nextWidth = startSelection.width;
  let nextHeight = startSelection.height;

  if (direction.includes('w')) {
    nextX = clamp(startSelection.x + deltaX, 0, right - MIN_SELECTION_SIZE);
    nextWidth = right - nextX;
  }

  if (direction.includes('e')) {
    nextWidth = clamp(startSelection.width + deltaX, MIN_SELECTION_SIZE, bounds.width - startSelection.x);
  }

  if (direction.includes('n')) {
    nextY = clamp(startSelection.y + deltaY, 0, bottom - MIN_SELECTION_SIZE);
    nextHeight = bottom - nextY;
  }

  if (direction.includes('s')) {
    nextHeight = clamp(startSelection.height + deltaY, MIN_SELECTION_SIZE, bounds.height - startSelection.y);
  }

  return {
    x: nextX,
    y: nextY,
    width: nextWidth,
    height: nextHeight
  };
}

export default function SecondaryPictureFreeCropper({
  source,
  exitCrop,
  setCroppedFile,
  continueLabel,
  exitLabel,
  isLoading = false
}) {
  const t = useTranslations('common.forms.add_artwork');
  const resolvedContinueLabel = continueLabel || t('media.choose_crop');
  const resolvedExitLabel = exitLabel || t('media.exit_crop');
  const wrapperRef = useRef(null);
  const imageRef = useRef(null);
  const overlayRef = useRef(null);
  const interactionRef = useRef(null);
  const previousSelectionRef = useRef(null);

  const [displayBounds, setDisplayBounds] = useState(null);
  const [naturalSize, setNaturalSize] = useState(null);
  const [selection, setSelection] = useState(null);

  const updateDisplayBounds = useCallback(() => {
    if (!wrapperRef.current || !imageRef.current) {
      return;
    }

    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const imageRect = imageRef.current.getBoundingClientRect();

    if (!wrapperRect.width || !wrapperRect.height || !imageRect.width || !imageRect.height) {
      return;
    }

    setDisplayBounds({
      left: imageRect.left - wrapperRect.left,
      top: imageRect.top - wrapperRect.top,
      width: imageRect.width,
      height: imageRect.height
    });
  }, []);

  useEffect(() => {
    setSelection(null);
    setDisplayBounds(null);
    setNaturalSize(null);
  }, [source]);

  useEffect(() => {
    const handleWindowResize = () => {
      updateDisplayBounds();
    };

    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [updateDisplayBounds]);

  function getLocalPoint(event) {
    if (!overlayRef.current) {
      return null;
    }

    const overlayRect = overlayRef.current.getBoundingClientRect();
    const localX = clamp(event.clientX - overlayRect.left, 0, overlayRect.width);
    const localY = clamp(event.clientY - overlayRect.top, 0, overlayRect.height);

    return {
      x: localX,
      y: localY
    };
  }

  function startInteraction(event, payload) {
    if (!overlayRef.current) {
      return;
    }

    const localPoint = getLocalPoint(event);
    if (!localPoint) {
      return;
    }

    const interaction = {
      pointerId: event.pointerId,
      startX: localPoint.x,
      startY: localPoint.y,
      hasMoved: false,
      ...payload
    };

    interactionRef.current = interaction;
    overlayRef.current.setPointerCapture(event.pointerId);
  }

  function handleOverlayPointerDown(event) {
    if (!displayBounds) {
      return;
    }

    previousSelectionRef.current = selection;
    startInteraction(event, {
      type: 'draw'
    });
  }

  function handleSelectionPointerDown(event) {
    if (!selection) {
      return;
    }

    event.stopPropagation();
    startInteraction(event, {
      type: 'move',
      initialSelection: selection
    });
  }

  function handleResizePointerDown(event, direction) {
    if (!selection) {
      return;
    }

    event.stopPropagation();
    startInteraction(event, {
      type: 'resize',
      direction,
      initialSelection: selection
    });
  }

  function handleOverlayPointerMove(event) {
    const interaction = interactionRef.current;
    if (!interaction || interaction.pointerId !== event.pointerId || !displayBounds) {
      return;
    }

    const localPoint = getLocalPoint(event);
    if (!localPoint) {
      return;
    }

    const deltaX = localPoint.x - interaction.startX;
    const deltaY = localPoint.y - interaction.startY;
    interaction.hasMoved = interaction.hasMoved || Math.abs(deltaX) >= DRAG_THRESHOLD || Math.abs(deltaY) >= DRAG_THRESHOLD;

    if (interaction.type === 'draw') {
      const left = clamp(Math.min(interaction.startX, localPoint.x), 0, displayBounds.width);
      const top = clamp(Math.min(interaction.startY, localPoint.y), 0, displayBounds.height);
      const right = clamp(Math.max(interaction.startX, localPoint.x), 0, displayBounds.width);
      const bottom = clamp(Math.max(interaction.startY, localPoint.y), 0, displayBounds.height);

      setSelection({
        x: left,
        y: top,
        width: Math.max(1, right - left),
        height: Math.max(1, bottom - top)
      });
      return;
    }

    if (interaction.type === 'move' && interaction.initialSelection) {
      const maxX = displayBounds.width - interaction.initialSelection.width;
      const maxY = displayBounds.height - interaction.initialSelection.height;

      setSelection({
        ...interaction.initialSelection,
        x: clamp(interaction.initialSelection.x + deltaX, 0, maxX),
        y: clamp(interaction.initialSelection.y + deltaY, 0, maxY)
      });
      return;
    }

    if (interaction.type === 'resize' && interaction.initialSelection) {
      setSelection(
        resolveResizeSelection(
          interaction.initialSelection,
          interaction.direction,
          deltaX,
          deltaY,
          displayBounds
        )
      );
    }
  }

  function handleOverlayPointerUp(event) {
    const interaction = interactionRef.current;
    if (!interaction || interaction.pointerId !== event.pointerId) {
      return;
    }

    if (interaction.type === 'draw') {
      if (!interaction.hasMoved) {
        setSelection(previousSelectionRef.current);
      } else {
        setSelection(prevValue => {
          if (!prevValue) {
            return prevValue;
          }

          const width = Math.max(MIN_SELECTION_SIZE, prevValue.width);
          const height = Math.max(MIN_SELECTION_SIZE, prevValue.height);

          return {
            ...prevValue,
            width: Math.min(width, displayBounds.width - prevValue.x),
            height: Math.min(height, displayBounds.height - prevValue.y)
          };
        });
      }
    }

    if (overlayRef.current && overlayRef.current.hasPointerCapture(event.pointerId)) {
      overlayRef.current.releasePointerCapture(event.pointerId);
    }

    interactionRef.current = null;
  }

  async function handleCropConfirm() {
    if (!source || !displayBounds || !naturalSize) {
      return;
    }

    const scaleX = naturalSize.width / displayBounds.width;
    const scaleY = naturalSize.height / displayBounds.height;

    const cropSelection = selection || {
      x: 0,
      y: 0,
      width: displayBounds.width,
      height: displayBounds.height
    };

    const cropX = clamp(Math.round(cropSelection.x * scaleX), 0, naturalSize.width - 1);
    const cropY = clamp(Math.round(cropSelection.y * scaleY), 0, naturalSize.height - 1);
    const cropWidth = clamp(Math.round(cropSelection.width * scaleX), 1, naturalSize.width - cropX);
    const cropHeight = clamp(Math.round(cropSelection.height * scaleY), 1, naturalSize.height - cropY);

    const croppedBlob = await getCroppedImgData(source, {
      x: cropX,
      y: cropY,
      width: cropWidth,
      height: cropHeight
    });

    const croppedFile = new File([croppedBlob], 'croppedImage.png', { type: 'image/png' });
    setCroppedFile(croppedFile);
  }

  return (
    <>
      <div className="secondary-picture-free-cropper__wrapper" ref={wrapperRef}>
        <img
          ref={imageRef}
          src={source}
          alt={t('media.secondary_picture_crop_alt')}
          className="secondary-picture-free-cropper__image"
          draggable={false}
          onLoad={(event) => {
            const target = event.currentTarget;
            setNaturalSize({
              width: target.naturalWidth || target.width || 1,
              height: target.naturalHeight || target.height || 1
            });
            updateDisplayBounds();
          }}
        />

        {displayBounds && (
          <div
            ref={overlayRef}
            className="secondary-picture-free-cropper__overlay"
            style={{
              left: displayBounds.left,
              top: displayBounds.top,
              width: displayBounds.width,
              height: displayBounds.height,
            }}
            onPointerDown={handleOverlayPointerDown}
            onPointerMove={handleOverlayPointerMove}
            onPointerUp={handleOverlayPointerUp}
            onPointerCancel={handleOverlayPointerUp}
          >
            {selection && (
              <div
                className="secondary-picture-free-cropper__selection"
                style={{
                  left: selection.x,
                  top: selection.y,
                  width: selection.width,
                  height: selection.height,
                }}
                onPointerDown={handleSelectionPointerDown}
              >
                {HANDLE_DIRECTIONS.map(direction => (
                  <button
                    key={direction}
                    type="button"
                    className={`secondary-picture-free-cropper__handle ${direction}`}
                    onPointerDown={event => handleResizePointerDown(event, direction)}
                    aria-label={t('media.resize_crop_handle', { direction })}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="artwork-options__download-template-templates-crop-options">
        <div className="artwork-options__footer-background">
          <p onClick={exitCrop}>{resolvedExitLabel}</p>
          <ContinueButton
            loading={isLoading}
            className="choose-action"
            continueClass={false}
            onClick={handleCropConfirm}
          >
            {resolvedContinueLabel}
          </ContinueButton>
        </div>
      </div>
    </>
  );
}
